import { supabase } from '../lib/supabase';
import type {
  ProjectoCampaign,
  ProjectoCampaignInsert,
  ProjectoPLItem,
  ProjectoPLItemInsert,
  ProjectoInvoice,
  ProjectoInvoiceInsert,
  ProjectoActivityLog,
  ProjectoPLSummary,
} from '../types/projecto';

// Columns a user may edit on a project. Anything else the form holds
// (id, work_code, created_by, created_at, updated_at) must never be sent.
const PROJECTO_CAMPAIGN_EDITABLE_FIELDS = [
  'name',
  'client_id',
  'client_name',
  'brand',
  'reference',
  'status',
  'start_date',
  'finish_date',
  'notes',
  'drive_link',
  'budget_enabled',
  'budget_amount',
  'target_margin_pct',
  'admin_only',
] as const;

function sanitizeProjectoCampaignUpdates(
  updates: Partial<ProjectoCampaignInsert>
): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const key of PROJECTO_CAMPAIGN_EDITABLE_FIELDS) {
    if (key in updates) clean[key] = (updates as Record<string, unknown>)[key];
  }
  return clean;
}

// ─── Campaigns ────────────────────────────────────────────────────────────────

export const projectoCampaignService = {
  async getAll(): Promise<ProjectoCampaign[]> {
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data ?? [];
  },

  async getById(id: string): Promise<ProjectoCampaign | null> {
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async generateNextWorkCode(year: number): Promise<string> {
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .select('work_code')
      .like('work_code', `P${year}/%`)
      .order('created_at', { ascending: false });

    if (error) throw error;

    let maxSequence = 0;
    if (data && data.length > 0) {
      data.forEach((item: { work_code: string | null }) => {
        if (!item.work_code) return;
        const match = item.work_code.match(/\/(\d+)/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxSequence) maxSequence = num;
        }
      });
    }

    return `P${year}/${maxSequence + 1}`;
  },

  async claimNextWorkCode(year: number): Promise<string> {
    const { data, error } = await supabase.rpc('generate_next_projecto_work_code', { p_year: year });
    if (error) throw error;
    return data;
  },

  async create(insert: ProjectoCampaignInsert): Promise<ProjectoCampaign> {
    const { data: { user } } = await supabase.auth.getUser();
    const year = new Date().getFullYear();
    const workCode = await projectoCampaignService.claimNextWorkCode(year);
    const { data, error } = await supabase
      .from('projecto_campaigns')
      .insert({ ...insert, work_code: workCode, created_by: user?.id ?? null })
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(data.id, user?.email ?? '', 'created', {});
    return data;
  },

  async update(id: string, updates: Partial<ProjectoCampaignInsert>): Promise<ProjectoCampaign> {
    const { data: { user } } = await supabase.auth.getUser();

    // Callers pass whole form state, which carries read-only columns
    // (id, work_code, created_by, created_at, updated_at) and sometimes keys
    // that are not columns at all - PostgREST rejects the whole request then.
    const clean = sanitizeProjectoCampaignUpdates(updates);
    if (Object.keys(clean).length === 0) {
      const current = await projectoCampaignService.getById(id);
      if (!current) throw new Error('Project not found');
      return current;
    }

    const { data, error } = await supabase
      .from('projecto_campaigns')
      .update(clean)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(id, user?.email ?? '', 'updated', { fields: Object.keys(updates) });
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('projecto_campaigns')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── P&L Items ────────────────────────────────────────────────────────────────

export const projectoPLService = {
  async getByProjectoId(projectoId: string): Promise<ProjectoPLItem[]> {
    const { data, error } = await supabase
      .from('projecto_pl_items')
      .select('*')
      .eq('projecto_id', projectoId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data ?? [];
  },

  async create(insert: ProjectoPLItemInsert): Promise<ProjectoPLItem> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('projecto_pl_items')
      .insert(insert)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(
      insert.projecto_id,
      user?.email ?? '',
      'item_added',
      { item_name: data.name, is_revenue: data.is_revenue }
    );
    return data;
  },

  async update(id: string, projectoId: string, updates: Partial<ProjectoPLItemInsert>): Promise<ProjectoPLItem> {
    const { data, error } = await supabase
      .from('projecto_pl_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string, projectoId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: item } = await supabase.from('projecto_pl_items').select('name').eq('id', id).maybeSingle();

    const { error } = await supabase
      .from('projecto_pl_items')
      .delete()
      .eq('id', id);

    if (error) throw error;

    await projectoActivityService.log(projectoId, user?.email ?? '', 'item_removed', { item_name: item?.name ?? '' });
  },

  async updateOrder(items: { id: string; display_order: number }[]): Promise<void> {
    const updates = items.map(({ id, display_order }) =>
      supabase.from('projecto_pl_items').update({ display_order }).eq('id', id)
    );
    await Promise.all(updates);
  },
};

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const projectoInvoiceService = {
  async getByProjectoId(projectoId: string): Promise<ProjectoInvoice[]> {
    const { data, error } = await supabase
      .from('projecto_invoices')
      .select('*')
      .eq('projecto_id', projectoId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    const now = new Date();
    return (data ?? []).map((inv) => ({
      ...inv,
      is_overdue:
        (inv.status === 'Issued' || inv.status === 'Partially Paid') &&
        inv.due_date != null &&
        new Date(inv.due_date) < now,
    }));
  },

  async create(insert: ProjectoInvoiceInsert): Promise<ProjectoInvoice> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('projecto_invoices')
      .insert(insert)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(
      insert.projecto_id,
      user?.email ?? '',
      'invoice_added',
      { invoice_type: insert.invoice_type, number: insert.number }
    );
    return data;
  },

  async update(id: string, projectoId: string, updates: Partial<ProjectoInvoiceInsert>): Promise<ProjectoInvoice> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('projecto_invoices')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    await projectoActivityService.log(projectoId, user?.email ?? '', 'invoice_updated', { fields: Object.keys(updates) });
    return data;
  },

  async delete(id: string, projectoId: string): Promise<void> {
    const { error } = await supabase
      .from('projecto_invoices')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── Activity Logs ────────────────────────────────────────────────────────────

export const projectoActivityService = {
  async getByProjectoId(projectoId: string): Promise<ProjectoActivityLog[]> {
    const { data, error } = await supabase
      .from('projecto_activity_logs')
      .select('*')
      .eq('projecto_id', projectoId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data ?? [];
  },

  async log(
    projectoId: string,
    userEmail: string,
    action: ProjectoActivityLog['action'],
    details: Record<string, unknown>
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('projecto_activity_logs').insert({
      projecto_id: projectoId,
      user_id: user?.id ?? null,
      user_email: userEmail,
      action,
      details,
    });
  },
};

// ─── P&L Calculator ───────────────────────────────────────────────────────────

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

export function getItemCommissionAmount(item: ProjectoPLItem): number {
  if (item.entry_kind === 'revenue' || item.is_revenue) return 0;
  if (item.entry_kind === 'passthrough') {
    const spend = getPassthroughSpend(item);
    return round2(Math.max(0, (item.total_price ?? 0) - spend));
  }
  if (item.cost_commission_mode === 'flat') {
    return round2(item.cost_commission_flat ?? 0);
  }
  return round2(item.total_price * ((item.cost_commission_pct ?? 0) / 100));
}

export function getPassthroughSpend(item: ProjectoPLItem): number {
  const list = Array.isArray(item.passthrough_breakdown) ? item.passthrough_breakdown : [];
  return round2(list.reduce((s, c) => s + (Number(c?.amount) || 0), 0));
}

export function getItemEntryKind(item: ProjectoPLItem): 'revenue' | 'cost' | 'passthrough' {
  if (item.entry_kind) return item.entry_kind;
  if (item.is_revenue) return 'revenue';
  if (item.cost_billing_mode === 'reimbursed') return 'passthrough';
  return 'cost';
}

export function computeProjectoPLSummary(
  items: ProjectoPLItem[],
  invoices: ProjectoInvoice[]
): ProjectoPLSummary {
  const revenueItems = items.filter((i) => getItemEntryKind(i) === 'revenue');
  const passthroughItems = items.filter((i) => getItemEntryKind(i) === 'passthrough');
  const absorbedItems = items.filter((i) => getItemEntryKind(i) === 'cost');

  const baseRevenue = round2(revenueItems.reduce((s, i) => s + i.total_price, 0));
  // Absorbed costs only: pass-through margin already enters via
  // passthroughCharged − passthroughSpend, so counting it here doubled it
  const costCommissionRevenue = round2(
    absorbedItems.reduce((s, i) => s + getItemCommissionAmount(i), 0)
  );
  const passthroughCharged = round2(passthroughItems.reduce((s, i) => s + i.total_price, 0));
  const passthroughSpend = round2(passthroughItems.reduce((s, i) => s + getPassthroughSpend(i), 0));
  const passthroughCommission = round2(Math.max(0, passthroughCharged - passthroughSpend));
  const reimbursedCosts = passthroughCharged;
  const absorbedCosts = round2(absorbedItems.reduce((s, i) => s + i.total_price, 0));
  const totalCosts = round2(absorbedCosts + passthroughSpend);

  const billableToClient = round2(baseRevenue + costCommissionRevenue + passthroughCharged);
  const totalRevenue = billableToClient;

  const weightedRevenueMargin = round2(
    revenueItems.reduce((s, i) => s + (i.margin_pct / 100) * i.total_price, 0)
  );

  // Pass-through commission (charged − spent) is agency profit too; it left
  // costCommissionRevenue when that stopped double-counting it in the
  // amount billed, so it is added here explicitly.
  const netProfit = round2(weightedRevenueMargin + costCommissionRevenue + passthroughCommission - absorbedCosts);
  const netMarginPct = billableToClient > 0 ? round2((netProfit / billableToClient) * 100) : 0;

  const grossProfit = round2(billableToClient - totalCosts);
  const grossMarginPct = billableToClient > 0 ? round2((grossProfit / billableToClient) * 100) : 0;

  const agencyMargin = round2(weightedRevenueMargin + costCommissionRevenue + passthroughCommission);
  const agencyMarginPct = billableToClient > 0 ? round2((agencyMargin / billableToClient) * 100) : 0;

  const outstandingReceivables = round2(invoices
    .filter((i) => i.invoice_type === 'Receivable' && i.status !== 'Paid' && i.status !== 'Cancelled')
    .reduce((s, i) => s + (i.total - i.amount_paid), 0));

  const outstandingPayables = round2(invoices
    .filter((i) => i.invoice_type === 'Payable' && i.status !== 'Paid' && i.status !== 'Cancelled')
    .reduce((s, i) => s + (i.total - i.amount_paid), 0));

  const netCashPosition = round2(outstandingReceivables - outstandingPayables);

  // Paid costs: absorbed items marked Paid, plus pass-through spend lines
  // ticked "Pago" in their breakdown
  const passthroughPaid = passthroughItems.reduce((s, i) => {
    const list = Array.isArray(i.passthrough_breakdown) ? i.passthrough_breakdown : [];
    return s + list.filter((c) => c?.paid).reduce((t, c) => t + (Number(c?.amount) || 0), 0);
  }, 0);
  const paidCosts = round2(absorbedItems
    .filter((i) => i.payment_status === 'Paid')
    .reduce((s, i) => s + i.total_price, 0) + passthroughPaid);

  const pendingCosts = round2(Math.max(0, totalCosts - paidCosts));

  return {
    totalRevenue,
    totalCosts,
    absorbedCosts,
    reimbursedCosts,
    passthroughSpend,
    passthroughCommission,
    costCommissionRevenue,
    billableToClient,
    grossProfit,
    grossMarginPct,
    netProfit,
    netMarginPct,
    agencyMargin,
    agencyMarginPct,
    outstandingReceivables,
    outstandingPayables,
    netCashPosition,
    paidCosts,
    pendingCosts,
  };
}
